import { createAction, createAsyncThunk, Reducer } from '@reduxjs/toolkit'
import { AddWasteContainerMeasurementDto, CloseWasteContainerDto, DisposeWasteContainerDto, WasteContainerLogDto, WasteContainerMeasurementDto, TransferWasteContainerDto, WasteContainerDto, CreateWasteContainerDto, UpdateWasteContainerDto } from '@services/dtos'
import { baseCrudReducer, CrudState, DEFAULT_FILTERS } from './baseCrudReducer'
import { wasteContainerService, WasteContainersFilters } from '@services/wasteContainerService'
import { PaginatedQueryBase } from '@services/api'
import { PaginatedDto } from '@services/extendedType'

export type WasteContainerLogsFilters = PaginatedQueryBase & {
  containerId?: string
}

export type WasteContainerMeasurementsFilters = PaginatedQueryBase & {
  containerId?: string
}

export type WasteContainerState = CrudState<WasteContainerDto, WasteContainersFilters> & {
  logs: PaginatedDto<WasteContainerLogDto> | undefined
  logsLoading: boolean
  logsFilters: WasteContainerLogsFilters
  measurements: PaginatedDto<WasteContainerMeasurementDto> | undefined
  measurementsLoading: boolean
  measurementsFilters: WasteContainerMeasurementsFilters
}

export const setWasteContainerLogsFilters = createAction<Partial<WasteContainerLogsFilters>>('wasteContainer/setLogsFilters')
export const setWasteContainerMeasurementsFilters = createAction<Partial<WasteContainerMeasurementsFilters>>('wasteContainer/setMeasurementsFilters')

export const getWasteContainerLogs = createAsyncThunk<PaginatedDto<WasteContainerLogDto>, void>(
  'wasteContainer/getLogs',
  async (_, { getState }) => {
    const { logsFilters } = (getState() as { wasteContainer: WasteContainerState }).wasteContainer
    const { containerId, ...pagination } = logsFilters ?? DEFAULT_FILTERS
    if (!containerId) {
      return { datas: [], totalCount: 0 }
    }
    return wasteContainerService.getLogs(containerId, pagination)
  },
)

export const getWasteContainerMeasurements = createAsyncThunk<PaginatedDto<WasteContainerMeasurementDto>, void>(
  'wasteContainer/getMeasurements',
  async (_, { getState }) => {
    const { measurementsFilters } = (getState() as { wasteContainer: WasteContainerState }).wasteContainer
    const { containerId, ...pagination } = measurementsFilters ?? DEFAULT_FILTERS
    if (!containerId) {
      return { datas: [], totalCount: 0 }
    }
    return wasteContainerService.getMeasurements(containerId, pagination)
  },
)

const { reducer, actions, thunks } = baseCrudReducer<
  WasteContainerDto,
  CreateWasteContainerDto,
  UpdateWasteContainerDto,
  WasteContainersFilters
>('wasteContainer', wasteContainerService, {
  extraReducers: (b) => {
    b
      .addCase(setWasteContainerLogsFilters, (state, action) => {
        (state as unknown as WasteContainerState).logsFilters = { ...(state as unknown as WasteContainerState).logsFilters, page: 0, ...action.payload }
      })
      .addCase(getWasteContainerLogs.pending, (state) => { (state as unknown as WasteContainerState).logsLoading = true })
      .addCase(getWasteContainerLogs.rejected, (state) => { (state as unknown as WasteContainerState).logsLoading = false })
      .addCase(getWasteContainerLogs.fulfilled, (state, action) => {
        const s = state as unknown as WasteContainerState
        s.logs = action.payload
        s.logsLoading = false
      })
      .addCase(setWasteContainerMeasurementsFilters, (state, action) => {
        (state as unknown as WasteContainerState).measurementsFilters = { ...(state as unknown as WasteContainerState).measurementsFilters, page: 0, ...action.payload }
      })
      .addCase(getWasteContainerMeasurements.pending, (state) => { (state as unknown as WasteContainerState).measurementsLoading = true })
      .addCase(getWasteContainerMeasurements.rejected, (state) => { (state as unknown as WasteContainerState).measurementsLoading = false })
      .addCase(getWasteContainerMeasurements.fulfilled, (state, action) => {
        const s = state as unknown as WasteContainerState
        s.measurements = action.payload
        s.measurementsLoading = false
      })
  },
})

export default reducer as unknown as Reducer<WasteContainerState>

export const { setFilters: setWasteContainerFilters } = actions

export const {
  getAll: getWasteContainers,
  create: createWasteContainer,
  update: updateWasteContainer,
  remove: removeWasteContainer,
} = thunks

export const addWasteContainerMeasurement = createAsyncThunk<void, { id: string; dto: AddWasteContainerMeasurementDto }>(
  'wasteContainer/addMeasurement',
  async ({ id, dto }) => {
    await wasteContainerService.addMeasurement(id, dto)
  },
)

export const closeWasteContainer = createAsyncThunk<WasteContainerDto, { id: string; dto: CloseWasteContainerDto }>(
  'wasteContainer/close',
  async ({ id, dto }, { dispatch }) => {
    const result = await wasteContainerService.close(id, dto)
    dispatch(thunks.getAll())
    return result
  },
)

export const disposeWasteContainer = createAsyncThunk<WasteContainerDto, { id: string; dto: DisposeWasteContainerDto }>(
  'wasteContainer/dispose',
  async ({ id, dto }, { dispatch }) => {
    const result = await wasteContainerService.dispose(id, dto)
    dispatch(thunks.getAll())
    return result
  },
)

export const transferWasteContainer = createAsyncThunk<WasteContainerDto, { id: string; dto: TransferWasteContainerDto }>(
  'wasteContainer/transfer',
  async ({ id, dto }, { dispatch }) => {
    const result = await wasteContainerService.transfer(id, dto)
    dispatch(thunks.getAll())
    return result
  },
)

export const cancelDisposeWasteContainer = createAsyncThunk<WasteContainerDto, string>(
  'wasteContainer/cancelDispose',
  async (id, { dispatch }) => {
    const result = await wasteContainerService.cancelDispose(id)
    dispatch(thunks.getAll())
    return result
  },
)

export const reopenWasteContainer = createAsyncThunk<WasteContainerDto, string>(
  'wasteContainer/reopen',
  async (id, { dispatch }) => {
    const result = await wasteContainerService.reopen(id)
    dispatch(thunks.getAll())
    return result
  },
)

export const cancelTransferWasteContainer = createAsyncThunk<WasteContainerDto, string>(
  'wasteContainer/cancelTransfer',
  async (id, { dispatch }) => {
    const result = await wasteContainerService.cancelTransfer(id)
    dispatch(thunks.getAll())
    return result
  },
)
